import type { PersonalPageCapabilities } from '../capabilities/types';

export const UNLIMITED_TICKET_CAPACITY = 99999;

export function isTicketUnlimited(capacity: number): boolean {
  return capacity === -1 || capacity >= UNLIMITED_TICKET_CAPACITY;
}

export interface TicketCapacitySource {
  initialCapacity?: number | null;
  totalAvailable?: number | null;
  totalSold?: number | null;
}

export function resolveTicketCapacity(ticket: TicketCapacitySource): number {
  if (ticket.initialCapacity != null) {
    return ticket.initialCapacity;
  }
  const remaining = ticket.totalAvailable ?? 0;
  const sold = ticket.totalSold ?? 0;
  return remaining + sold;
}

export function sumTicketsInitialCapacity(tickets: ReadonlyArray<{ initialCapacity?: number | null }>): number {
  return tickets.reduce((sum, ticket) => {
    const capacity = ticket.initialCapacity ?? 0;
    if (capacity <= 0 || isTicketUnlimited(capacity)) {
      return sum;
    }
    return sum + capacity;
  }, 0);
}

function hasUnlimitedTicketStock(tickets: ReadonlyArray<{ initialCapacity?: number | null }>): boolean {
  return tickets.some(ticket => {
    const capacity = ticket.initialCapacity;
    if (capacity == null || capacity === 0) {
      return true;
    }
    return isTicketUnlimited(capacity);
  });
}

export interface ResolveEventTotalCapacityInput {
  event: {
    isUnlimitedRegistration?: boolean;
    registrationLimit?: number | null;
    tickets?: ReadonlyArray<{ initialCapacity?: number | null }>;
  };
  capabilities: Pick<PersonalPageCapabilities, 'itemOptions'>;
  ticketsTotalCapacity?: number;
}

export interface PageRegistrationCapacityInput {
  isUnlimitedRegistration?: boolean;
  registrationLimit?: number | null;
}

function pageCapacity(
  capabilities: Pick<PersonalPageCapabilities, 'itemOptions'>,
  page: PageRegistrationCapacityInput
): number {
  if (!capabilities.itemOptions.inventory) {
    return 0;
  }
  if (page.isUnlimitedRegistration) {
    return 0;
  }
  const limit = page.registrationLimit ?? 0;
  if (limit <= 0) {
    return 0;
  }
  return limit;
}

export function resolveInventoryCapacityCeiling(
  page: PageRegistrationCapacityInput,
  capabilities: Pick<PersonalPageCapabilities, 'itemOptions'>,
  tickets?: ReadonlyArray<{ initialCapacity?: number | null }>,
  ticketsTotalCapacity?: number
): number {
  const pageCap = pageCapacity(capabilities, page);

  if (pageCap === 0) {
    return 0;
  }

  const ticketList = tickets ?? [];

  if (ticketList.length > 0 && hasUnlimitedTicketStock(ticketList)) {
    return pageCap;
  }

  const ticketsCapacity = ticketsTotalCapacity ?? sumTicketsInitialCapacity(ticketList);
  const caps = [ticketsCapacity, pageCap].filter(capacity => capacity > 0);

  if (caps.length === 0) {
    return 0;
  }
  return Math.min(...caps);
}

export function resolveEventTotalCapacity(input: ResolveEventTotalCapacityInput): number {
  const { capabilities, ticketsTotalCapacity, event } = input;
  return resolveInventoryCapacityCeiling(
    {
      isUnlimitedRegistration: event.isUnlimitedRegistration,
      registrationLimit: event.registrationLimit
    },
    capabilities,
    event.tickets,
    ticketsTotalCapacity
  );
}

export function checkPageRegistrationCapacity(
  page: PageRegistrationCapacityInput,
  capabilities: Pick<PersonalPageCapabilities, 'itemOptions'>,
  currentTotalSold: number,
  requestedQuantity: number,
  tickets?: ReadonlyArray<{ initialCapacity?: number | null }>,
  ticketsTotalCapacity?: number
): string | null {
  if (!capabilities.itemOptions.inventory) {
    return null;
  }

  const effectiveCap = resolveInventoryCapacityCeiling(page, capabilities, tickets, ticketsTotalCapacity);
  if (effectiveCap <= 0) {
    return null;
  }

  const remaining = effectiveCap - currentTotalSold;
  if (remaining <= 0) {
    return 'הגעתם למכסה הכוללת של הדף';
  }
  if (requestedQuantity > remaining) {
    return `ניתן להוסיף עוד ${remaining} בלבד (ביקשתם ${requestedQuantity})`;
  }
  return null;
}
